/* differ* — the fixed-width canvas.

   Lays the homepage out once at a design width, then scales the rendered
   result. See differ-canvas.css for why this is a transform rather than a
   fluid root font-size.

   Deliberate constraints, each of them learned the hard way on this theme:

   - No prefers-reduced-motion guard. The canvas is layout, not motion. A phone
     with Reduce Motion on must get the identical composition, or it becomes a
     second code path that nobody can see to test.
   - No ResizeObserver. It has never fired in this theme, not even its initial
     callback. Plain events plus fixed timeouts and a height poll do fire.
   - Positions travel as CSS custom properties, never as inline left/top/width.
     An inline declaration outranks every stylesheet rule and each device
     override then loses silently.
*/
(function () {
  'use strict';

  var PHONE = 390;
  var DESKTOP = 1440;
  var BREAKPOINT = 1024;

  /* Design viewport heights, the second half of the canvas unit system.
     A taller screen shows more page at once — that is aspect ratio and it
     cannot be designed away — but a height written in these units is the same
     fraction of the composition everywhere. */
  var PHONE_H = 844;
  var DESKTOP_H = 900;

  var root = document.documentElement;
  var stage = document.querySelector('[data-differ-stage]');
  var canvas = document.querySelector('[data-differ-canvas]');
  if (!stage || !canvas) return;

  root.classList.add('differ-canvas-on');

  var lastH = 0;

  /* The join bar is authored inside chapter 10 so its label stays editable,
     but position:fixed cannot survive inside a transformed ancestor. Move the
     node — not a copy — into the portal, and do it again whenever the editor
     re-renders that section. */
  function portal() {
    var host = document.querySelector('[data-differ-portal-scale]');
    if (!host) return;
    var bar = document.querySelector('[data-differ-joinbar]');
    if (bar && bar.parentNode !== host) host.appendChild(bar);
  }

  function measure() {
    /* clientWidth, not innerWidth: it excludes a classic scrollbar, so the
       canvas never ends up fractionally wider than the space it has. */
    var w = root.clientWidth || window.innerWidth;
    var design = w >= BREAKPOINT ? DESKTOP : PHONE;
    var scale = w / design;

    /* Cap at 1 above the desktop design width so type does not grow past the
       already-capped container; centre the canvas in the leftover space. */
    if (design === DESKTOP && scale > 1) scale = 1;

    var tx = Math.max(0, (w - design * scale) / 2);
    var designH = design === DESKTOP ? DESKTOP_H : PHONE_H;

    root.style.setProperty('--differ-canvas-w', design + 'px');
    root.style.setProperty('--differ-canvas-h', designH + 'px');
    root.style.setProperty('--differ-canvas-scale', scale);
    root.style.setProperty('--differ-canvas-tx', tx + 'px');

    /* theme.css keeps its viewport media queries for every other template, so
       the two rules that reach canvas content are restated against these. */
    root.classList.toggle('differ-canvas-phone', design === PHONE);
    root.classList.toggle('differ-canvas-desk', design === DESKTOP);

    /* A transform does not affect layout, so the stage would still be the
       untransformed height and the scrollbar would disagree with what is on
       screen. Set the scaled height explicitly. */
    var h = Math.ceil(canvas.scrollHeight * scale);
    if (h && h !== lastH) {
      stage.style.height = h + 'px';
      lastH = h;
      if (window.ScrollTrigger && window.ScrollTrigger.refresh) {
        window.ScrollTrigger.refresh();
      }
    }
    return h;
  }

  function sync() {
    portal();
    measure();
  }

  sync();

  /* Fonts, images and late sections all change the height after first paint. */
  [200, 700, 1600, 3000].forEach(function (ms) { window.setTimeout(sync, ms); });
  window.setInterval(measure, 1000);

  window.addEventListener('resize', sync);
  window.addEventListener('orientationchange', sync);
  window.addEventListener('load', sync);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(sync);

  /* Theme editor: re-portal and re-measure when a section is redrawn. */
  document.addEventListener('shopify:section:load', sync);
  document.addEventListener('shopify:section:unload', sync);
  document.addEventListener('shopify:section:reorder', sync);

  /* Exposed so the fraction checker can read the geometry it must normalise
     against, rather than guessing it from the viewport. */
  window.__differCanvas = function () {
    var w = root.clientWidth || window.innerWidth;
    var design = w >= BREAKPOINT ? DESKTOP : PHONE;
    var scale = w / design;
    if (design === DESKTOP && scale > 1) scale = 1;
    return { viewport: w, design: design, scale: scale, rendered: design * scale };
  };
})();
